/**
 * Is this person allowed to act on plan governance — asked in a way that
 * survives the workspace context arriving late.
 *
 * `canPerformPlanningGovernance()` reads module state that `ensureWorkspaceContext`
 * fills in a Layout effect — i.e. after the route's children have already
 * rendered. A component that called it during its first render got `false` and,
 * because nothing in React state had changed, never asked again: the person was
 * told "only workspace admins and owners can authorize" for the rest of the
 * page's life, with no way back but a fresh navigation that happened to lose the
 * race the other way. Observed on the governance page, where the Authorize
 * button simply was not there.
 *
 * `workspaceContextEvents` exists for exactly this ("a consumer that read null
 * on first render keeps the empty result forever"), so subscribe to it. The
 * snapshot is a boolean, so `useSyncExternalStore` re-renders only on a real
 * change.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { canPerformPlanningGovernance } from '../lib/planningService';
import { subscribeWorkspaceContext } from '../lib/workspaceContextEvents';

export function usePlanningGovernanceAllowed(cycleWorkspaceId?: string | null): boolean {
  const getSnapshot = useCallback(
    () => canPerformPlanningGovernance(cycleWorkspaceId),
    [cycleWorkspaceId],
  );
  return useSyncExternalStore(subscribeWorkspaceContext, getSnapshot, () => false);
}
